<template>
  <div>
    <v-form ref="form"
            v-model="valid"
            lazy-validation>
      <v-row class="my-0">
        <v-col cols="12"
               sm="6">
          <v-row class="my-0 mx-0">
            <LocalizedLabel>singleGame</LocalizedLabel>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field :label="translations.name"
                            v-model="game.name"
                            :rules="gameNameRules"
                            :disabled="isMultiGamesMode"
                            outlined
                            dense
                            required>
              </v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field :label="translations.displayName"
                            v-model="game.displayName"
                            :rules="gameDisplayNameRules"
                            :disabled="isMultiGamesMode"
                            outlined
                            dense
                            required>
              </v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-select :items="providers"
                        :label="translations.provider"
                        v-model="game.provider"
                        :rules="gameProviderRules"
                        :disabled="isMultiGamesMode"
                        outlined
                        dense
                        required>
              </v-select>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-divider></v-divider>
      <v-row class="my-6">
        <v-col>
          <v-row class="my-0 mb-5 mx-0">
            <LocalizedLabel>multipleGames</LocalizedLabel>
          </v-row>
          <v-textarea v-model="multipleGamesList"
                      :disabled="isSingleGameMode"
                      :rules="multiGamesRules"
                      clearable
                      outlined
                      height="500"
                      name="input-7-4"
                      label="Insert games list"
                      @click:clear="clearMultipleGamesList"
                      @blur="resetValidation">
          </v-textarea>
        </v-col>
      </v-row>
      <transition name="slide"
                  appear>
        <v-card class="fotterCard"
                tile
                elevation="10"
                v-if="detailsModalToggler">
          <v-row class="px-8 ma-0">
            <v-spacer></v-spacer>
            <v-card-actions>
              <v-btn text
                     small
                     class="text-none"
                     width="120"
                     @click="cancelButton()">
                <LocalizedLabel>cancel</LocalizedLabel>
              </v-btn>
              <v-btn small
                     color="primary"
                     class="background--text text-none"
                     width="120"
                     :key="actionKey"
                     :disabled="disabledAction"
                     @click="actionButton()">
                <LocalizedLabel>save</LocalizedLabel>
              </v-btn>
            </v-card-actions>
          </v-row>
        </v-card>
      </transition>
    </v-form>
    <v-dialog max-width="700"
              v-model="boostGlobalJackpotConfirmDialog">
      <v-card class="pa-6">
        <v-card-title class="pa-0 ml-2 my-4 subtitle--text">
          <LocalizedLabel>createNewGame</LocalizedLabel>
        </v-card-title>
        <v-row class="pa-3">
          <v-col cols="12"
                 sm="6">
            <span>{{ confirmInfo }}</span>
          </v-col>
        </v-row>
        <v-card-actions class="justify-end">
          <v-btn color="primary"
                 text
                 @click="boostGlobalJackpotConfirmDialog = false">
            <LocalizedLabel>back</LocalizedLabel>
          </v-btn>
          <v-btn color="primary"
                 text
                 :key="actionKey"
                 @click="createNewGame()">
            <LocalizedLabel>create</LocalizedLabel>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { isEntered } from '@/utility';

export default {
  name: 'BoostGlobalJackpotDetails',
  data() {
    return {
      valid: true,
      actionKey: 0,
      providers: [],
      game: {},
      multipleGamesList: '',
      disabledAction: false,
      boostGlobalJackpotConfirmDialog: false,
      boostGlobalJackpotDetails: {},
    };
  },
  computed: {
    ...mapGetters(['translations', 'detailsModalToggler', 'detailsModalConfig']),
    gameNameRules() {
      return [
        isEntered(this.game.name, this.translations.newGameNameErrorLabel),
      ];
    },
    gameDisplayNameRules() {
      return [
        isEntered(this.game.displayName, this.translations.newGameDisplayNameErrorLabel),
      ];
    },
    gameProviderRules() {
      return [
        isEntered(this.game.provider, this.translations.newGameProviderErrorLabel),
      ];
    },
    multiGamesRules() {
      return [
        isEntered(this.multipleGamesList, this.translations.newGameListErrorLabel) && this.isMultiGamesMode,
        (this.isValid(this.multipleGamesList) || this.translations.invalidJsonFormatErrorLabel),
      ];
    },
    isSingleGameMode() {
      return !!this.game.name || !!this.game.displayName;
    },
    isMultiGamesMode() {
      return !!this.multipleGamesList;
    },
    isValidSingleGame() {
      return !!this.game.name && !!this.game.displayName && !!this.game.provider;
    },
    isValidJson() {
      return this.isValid(this.multipleGamesList);
    },
    confirmInfo() {
      return this.isMultiGamesMode ? this.translations.createMultipleGames : this.game.displayName;
    },
  },
  methods: {
    ...mapActions([
      'toggleDetailsModal',
      'loadData',
      'createData',
      'setContentType',
    ]),
    clearMultipleGamesList() {
      this.multipleGamesList = '';
      this.$refs.form.reset();
    },
    resetValidation() {
      this.$refs.form.resetValidation();
    },
    cancelButton() {
      this.toggleDetailsModal(false);
    },
    async createNewGame() {
      if (this.isValidSingleGame || this.isValidJson) {
        const data = this.game;
        const payload = {
          notification: 'newGameCreatedLabel',
          notificationError: 'newGameInvalidSettings',
          newGameError: true,
          boost: true,
        };
        if (this.isSingleGameMode) {
          payload.data = data;
          payload.path = `tenants/${this.$route.query.company}/jackpot/games`;
        } else {
          payload.data = {
            games: JSON.parse(this.multipleGamesList),
          };
          payload.path = `tenants/${this.$route.query.company}/jackpot/games/actions/import`;
        }
        const response = await this.createData(payload);
        if (response) {
          this.boostGlobalJackpotConfirmDialog = false;
          this.toggleDetailsModal(false);
          this.actionKey += 1;
        }
      } else {
        this.boostGlobalJackpotConfirmDialog = false;
      }
    },
    async actionButton() {
      if (this.isValidJson) {
        this.boostGlobalJackpotConfirmDialog = true;
        this.actionKey += 1;
        return;
      }
      if (this.$refs.form.validate() && this.isValidSingleGame) {
        this.boostGlobalJackpotConfirmDialog = true;
        this.actionKey += 1;
      }
    },
    async getProviders() {
      const response = await this.loadData({
        path: `tenants/${this.$route.query.company}/product-providers`,
        boost: true,
      });
      this.providers = response.data;
    },
    isValid(str) {
      if (str) {
        try {
          JSON.parse(str);
        } catch (e) {
          return false;
        }
        return true;
      }
      return false;
    },
  },
  mounted() {
    this.getProviders();
  },
};
</script>
